import { useEffect, useState } from 'react';
import { Alert, Box, Grid, LinearProgress, Paper } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useRef } from 'react';
import { AutoCompleteFabrica, VFieldMoneyNumber2 } from '../../../shared/forms';
import { VForm, useVForm, IVFormErrors, VTextField } from '../../../shared/forms';
import { FerramentasDeDetalhe } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import { ProdutosService } from '../../../shared/services/api/cadastros/ProdutosService';

interface IFormData {
  descricao: string,
  ean: string,
  codfabrica: string,
  valor: number,
}
const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  descricao: yup.string().required().min(1).max(140),
  ean: yup.string().required(),
  codfabrica: yup.string().required(),
  valor: yup.number().required(),
});

export const DetalheDeProdutos: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const valorRef = useRef<HTMLInputElement>(null);
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [openmsg, setOpenmsg] = useState(false);
  const [openalert, setOpenAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [descricao, setDescricao] = useState<string>('');

  type RowsType = IFormData[];
  const handleCloseAlert = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;

    setOpenmsg(false);

    if (tms === 'success') {
      navigate('/produtos');
    }
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ProdutosService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            alert(result.message);
          } else {
            formRef.current?.setData(result);
            setDescricao(result.descricao);
          }
        });

    } else {
      formRef.current?.setData({
        idcultivo: '',
        descricao: '',
        ean: '',
        obs: '',
        status: true,
      });
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          ProdutosService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Cadastro Realizado com sucesso!');
                setOpenmsg(true);

                setTimeout(() => {
                }, 1500); // tempo do snackbar
              }
            });
        } else {
          ProdutosService
            .updateById(id, dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                alert(result.message);
              } else {
                setTms('success');
                setMsg('Cadastro Atualizado com sucesso!');
                setOpenmsg(true);

                setTimeout(() => {
                }, 1500); // tempo do snackbar
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};

        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);
      });
  };


  const handleGerar = async () => {
    setIsLoading(true);
    const result = await ProdutosService.geracodigobarra();
    setIsLoading(false);
    if (result instanceof Error) {
      alert(result.message);
      return '';
    } else {
      return result.gerar_ean13;
    }
  };


  const handleDelete = (id: string) => {
    if (confirm('Realmente deseja apagar?')) {
      ProdutosService.deleteById(id)
        .then(result => {
          if (result instanceof Error) {
            setMsg(result.message);
            setOpenAlert(true);
          } else {
            setMsg('Registro apagado com sucesso!');
            setOpenAlert(true);
            navigate('/produtos');
          }
        });
    }
  };

  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Novo Produto' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoImpressao={false}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressaoCarregando={isLoading}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/produtos')}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete(id)}
          aoClicarEmNovo={() => navigate('/produtos/novo')}
          mostrarBotaoNovoCarregando={isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <Snackbar
          open={openmsg}
          autoHideDuration={1500}
          onClose={handleCloseMsg}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
          >
            {msg}
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={1} spacing={2}>
            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid container item direction="row" spacing={1}>
              <Grid container item direction="row" spacing={1} >
                <Grid item xs={12} sm={12} md={4} lg={6} xl={6}>
                  <VTextField
                    fullWidth
                    size='small'
                    name='descricao'
                    disabled={isLoading}
                    label='Descrição'
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();

                        const descricaoValue = (e.target as HTMLInputElement).value;


                        formRef.current?.setFieldValue('codfabrica', descricaoValue);

                        const generatedEan = await handleGerar();
                        if (generatedEan) {

                          formRef.current?.setFieldValue('ean', generatedEan);
                        }

                        // Aguarda a renderização do input antes de focar
                        setTimeout(() => {
                          valorRef.current?.focus();
                        }, 1);
                      }
                    }}
                  />

                </Grid>
                <Grid item xs={12} sm={12} md={8} lg={6} xl={6}>
                  <VTextField
                    fullWidth
                    size='small'
                    name='ean'
                    disabled={isLoading}
                    label='Código EAN'
                  />
                </Grid>
              </Grid>
              <Grid container item direction="row" spacing={1} >

                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <AutoCompleteFabrica
                    isExternalLoading={isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                  <VTextField
                    fullWidth
                    size='small'
                    name='codfabrica'
                    disabled={isLoading}
                    label='Código Fabrica'
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                  <VFieldMoneyNumber2
                    inputRef={valorRef}
                    fullWidth
                    size='small'
                    name='valor'
                    disabled={isLoading}
                    label='Valor'
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>
    </LayoutBaseDePagina>
  );
};